/**
 * Labor category PDT upload hooks (presigned URL flow).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { paths } from '@access-cms/api-types';
import { PDT_UPLOAD_MIME, ResourceType } from '@access-cms/shared-constants';

import { get, patch, post } from '../../client';
import { queryKeys } from '../../queryKeys';
import type { LaborCategoryResponse } from './useLaborCategories';

type PdtUploadRequestPath = '/api/v1/contracts/{contractId}/labor-categories/{id}/pdt/upload-request';
type PdtUploadRequestBody = paths[PdtUploadRequestPath]['post']['requestBody']['content']['application/json'];
type PdtUploadRequestResponse = paths[PdtUploadRequestPath]['post']['responses']['201']['content']['application/json'];

type ClearPdtMetadataPath = '/api/v1/contracts/{contractId}/labor-categories/{id}/pdt';
type ClearPdtMetadataBody = paths[ClearPdtMetadataPath]['patch']['requestBody']['content']['application/json'];

export type LaborCategoryPdtUploadRequestData = NonNullable<PdtUploadRequestResponse['data']>;

export async function requestLaborCategoryPdtUpload(
  contractId: string,
  laborCategoryId: string,
  body: PdtUploadRequestBody,
): Promise<PdtUploadRequestResponse> {
  return post('/api/v1/contracts/{contractId}/labor-categories/{id}/pdt/upload-request', body, {
    pathParams: { contractId, id: laborCategoryId },
  });
}

export async function confirmLaborCategoryPdtUpload(
  contractId: string,
  laborCategoryId: string,
): Promise<LaborCategoryResponse> {
  return post(
    '/api/v1/contracts/{contractId}/labor-categories/{id}/pdt/confirm',
    {},
    {
      pathParams: { contractId, id: laborCategoryId },
    },
  );
}

type PdtDownloadPath = '/api/v1/contracts/{contractId}/labor-categories/{id}/pdt/download';
type PdtDownloadResponse = paths[PdtDownloadPath]['get']['responses']['200']['content']['application/json'];

export type LaborCategoryPdtDownloadUrlData = NonNullable<PdtDownloadResponse['data']>;

export async function getLaborCategoryPdtDownloadUrl(
  contractId: string,
  laborCategoryId: string,
): Promise<PdtDownloadResponse> {
  return get('/api/v1/contracts/{contractId}/labor-categories/{id}/pdt/download', {
    pathParams: { contractId, id: laborCategoryId },
  });
}

/** Clear abandoned/failed PDT metadata so the user can retry (does not remove confirmed PDT or S3 objects). */
export async function clearLaborCategoryPdtMetadata(
  contractId: string,
  laborCategoryId: string,
): Promise<LaborCategoryResponse> {
  const body: ClearPdtMetadataBody = { clear: true };

  return patch('/api/v1/contracts/{contractId}/labor-categories/{id}/pdt', body, {
    pathParams: { contractId, id: laborCategoryId },
  });
}

interface UseLaborCategoryPdtUploadOptions {
  contractId: string;
  onError?: (error: Error) => void;
}

/**
 * Full PDT flow: upload-request → client S3 PUT → confirm.
 */
export function useLaborCategoryPdtUpload({ contractId, onError }: UseLaborCategoryPdtUploadOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      laborCategoryId,
      pdtFileName,
    }: {
      file: File;
      laborCategoryId: string;
      pdtFileName: string;
    }) => {
      const uploadRequest = await requestLaborCategoryPdtUpload(contractId, laborCategoryId, {
        mimeType: 'application/pdf',
        pdtFileName,
        sizeBytes: file.size,
      });

      const uploadData = uploadRequest.data;
      if (!uploadData?.uploadUrl) {
        throw new Error('PDT upload request did not return an upload URL');
      }

      try {
        await uploadLaborCategoryPdtToS3(uploadData.uploadUrl, file);

        return confirmLaborCategoryPdtUpload(contractId, laborCategoryId);
      } catch (error) {
        console.error('[PDT] Labor category PDT upload flow failed', {
          contractId,
          error: error instanceof Error ? error.message : String(error),
          laborCategoryId,
        });
        try {
          await clearLaborCategoryPdtMetadata(contractId, laborCategoryId);
        } catch (clearError) {
          console.error('[PDT] Failed to clear labor category PDT metadata after upload failure:', clearError);
        }

        throw error;
      }
    },
    onError: (error: Error) => {
      onError?.(error);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys[ResourceType.CONTRACTS].nested(contractId).laborCategories.all,
      });
    },
  });
}

/**
 * Upload PDT bytes to S3 using the presigned PUT URL from upload-request.
 *
 * ACP-2145: the URL is signed for `application/pdf` exactly, so the header must
 * be the type the server authorized rather than whatever `file.type` the browser
 * reports — an empty or unusual `file.type` would otherwise desync from the
 * signature and S3 would answer 403.
 */
export async function uploadLaborCategoryPdtToS3(uploadUrl: string, file: File): Promise<void> {
  const response = await fetch(uploadUrl, {
    body: file,
    headers: {
      'Content-Type': PDT_UPLOAD_MIME,
    },
    method: 'PUT',
  });

  if (!response.ok) {
    const responseBody = await response.text().catch(() => '');
    console.error('[PDT] S3 PUT failed', {
      bodySnippet: responseBody.slice(0, 500),
      contentType: PDT_UPLOAD_MIME,
      fileSize: file.size,
      status: response.status,
      statusText: response.statusText,
    });
    throw new Error(`PDT upload to S3 failed (${response.status} ${response.statusText})`);
  }
}

/** @deprecated Use uploadLaborCategoryPdtToS3 */
export const uploadLaborCategoryPdtToS3Stub = uploadLaborCategoryPdtToS3;
